<!--
DashboardRecentDatasets

Owner-only recent dataset card. This keeps the commercial dashboard's
dataset-centric history intact while operational dashboards use the
separate Recent Views component.

Permission rules:
- summary.permissions.canUpload controls the owner upload tile.

@author Williams Francis <w.francis@nationaldrones.com> | <olawalewilliams9438@gmail.com>
-->
<template>
    <v-card class="workspace-dashboard__recent-card" elevation="0" data-home-tour-recent-datasets>
        <div class="workspace-dashboard__section-head">
            <div>
                <div class="workspace-dashboard__section-kicker">{{ sectionKicker }}</div>
                <h2>{{ sectionTitle }}</h2>
            </div>
            <v-btn variant="text" :href="summary.links?.viewer || '/view'">
                View all
            </v-btn>
        </div>

        <div v-if="datasetCards.length" class="workspace-dashboard__dataset-grid">
            <article
                v-for="dataset in displayDatasetCards"
                :key="dataset.id"
                class="workspace-dashboard__dataset-card"
            >
                <div class="workspace-dashboard__dataset-media">
                    <v-img
                        v-if="dataset.image"
                        :src="dataset.image"
                        :lazy-src="dataset.image"
                        cover
                        height="116"
                    />
                    <div v-else class="workspace-dashboard__dataset-fallback">
                        <v-icon size="28">mdi-image-outline</v-icon>
                    </div>
                    <span
                        class="workspace-dashboard__status workspace-dashboard__dataset-status"
                        :class="`workspace-dashboard__status--${dataset.statusTone}`"
                    >
                        {{ dataset.status }}
                    </span>
                </div>
                <div class="workspace-dashboard__dataset-card-body">
                    <h3>{{ dataset.name }}</h3>
                    <div class="workspace-dashboard__dataset-detail-block">
                        <span>Dataset Group</span>
                        <a :href="dataset.viewerUrl">SmartData Hosted</a>
                    </div>
                    <div class="workspace-dashboard__dataset-detail-block">
                        <span>Owned by</span>
                        <p>
                            <v-icon size="15">$vuetify.icons.briefCase</v-icon>
                            {{ summary.workspace?.name || dataset.workspace }}
                        </p>
                    </div>
                    <div class="workspace-dashboard__dataset-detail-block">
                        <span>Shared with Teams</span>
                        <strong>{{ primaryTeamName }}</strong>
                    </div>
                </div>
                <footer class="workspace-dashboard__dataset-footer">
                    <span>
                        <v-icon size="14">mdi-calendar-month-outline</v-icon>
                        {{ dataset.date || 'Recently updated' }}
                    </span>
                    <v-btn size="small" variant="outlined" :href="dataset.viewerUrl">
                        Open Viewer
                    </v-btn>
                </footer>
            </article>

            <a
                v-if="canUpload"
                class="workspace-dashboard__dataset-upload-tile"
                :href="summary.links?.upload || '/upload'"
            >
                <span class="workspace-dashboard__dataset-upload-icon">
                    <v-icon size="32">$vuetify.icons.upload</v-icon>
                </span>
                <strong>Upload New Dataset</strong>
                <p>Drag &amp; drop your dataset files here or click to browse</p>
                <span>Supported formats</span>
                <div class="workspace-dashboard__upload-platforms workspace-dashboard__upload-platforms--compact">
                    <span>DJI</span>
                    <span>Agisoft</span>
                    <span>Pix4D</span>
                    <span>RealityCapture</span>
                    <span>More</span>
                </div>
            </a>
            <div
                v-else
                class="workspace-dashboard__dataset-upload-tile"
            >
                <span class="workspace-dashboard__dataset-upload-icon">
                    <v-icon size="32">mdi-cloud-off-outline</v-icon>
                </span>
                <strong>{{ readonlyTileTitle }}</strong>
                <p>{{ readonlyTileCopy }}</p>
                <span>Viewer access remains available</span>
            </div>
        </div>
        <div v-else class="workspace-dashboard__empty-state">
            Your next uploaded dataset will appear here once it is ready to review.
        </div>
    </v-card>
</template>

<script>
export default {
    name: 'DashboardRecentDatasets',
    props: {
        summary: {
            type: Object,
            default: () => ({}),
        },
        datasetCards: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        permissions() {
            return this.summary.permissions || {};
        },
        canUpload() {
            return Boolean(this.permissions.canUpload);
        },
        sectionKicker() {
            return 'Recent datasets';
        },
        sectionTitle() {
            return 'Recent Datasets';
        },
        displayDatasetCards() {
            return this.datasetCards.slice(0, 2);
        },
        readonlyTileTitle() {
            return 'Upload access unavailable';
        },
        readonlyTileCopy() {
            return 'Uploads are currently hidden for this workspace role.';
        },
        primaryTeamName() {
            return (
                this.summary.teamPanel?.teams?.[0]?.name ||
                this.summary.workspace?.name ||
                'Operations Team'
            );
        },
    },
};
</script>
